import { computed, onBeforeUnmount, ref, shallowRef, useRuntimeConfig, watch } from '#imports';
import { useIntersectionObserver } from '@vueuse/core';
import type HighchartsNS from 'highcharts';
import { normalizeCurrency } from '@nuxt-shared/lib/chart-format';
import { fetchChartData } from './api';
import { buildChartOptions, buildSeries } from './lib';
import type {
  ChartApiResponse,
  ChartApiSeries,
  ChartSeries,
  ChartWidgetProps,
  ChartWidgetTab,
  ChartWidgetTranslate,
} from './types';

/**
 * Модель виджета графика.
 *
 * Виджет только рисует: что показывать, за какой период и в какой валюте —
 * решает контент-редактор в конструкторе админки, эти параметры приезжают
 * data-атрибутами блока и уходят в запрос. Переключателей на странице нет,
 * поэтому нет и работы с границами оси: рисуем ровно то, что вернул бэкенд.
 */

/** За сколько до вьюпорта начинать грузить данные. */
const PRELOAD_MARGIN = '200px';

export const useChartWidgetModel = (props: ChartWidgetProps, t: ChartWidgetTranslate) => {
  /*=== Загрузка данных ===*/

  const codes = computed<string[]>(() =>
    (Array.isArray(props.charts) ? props.charts : String(props.charts).split(','))
      .map((code) => code.trim())
      .filter(Boolean),
  );

  const root = ref<HTMLElement | null>(null);
  const chartEl = ref<HTMLElement | null>(null);
  const chartData = shallowRef<ChartApiResponse | null>(null);
  const loadStatus = ref<'idle' | 'pending' | 'ready' | 'error'>('idle');

  // Адрес API читаем на setup: сам запрос уходит из колбэка наблюдателя, где
  // композаблы Nuxt звать уже нельзя.
  const apiBase = useRuntimeConfig().public.apiBase as string;

  const load = async () => {
    if (loadStatus.value === 'pending' || loadStatus.value === 'ready') return;

    loadStatus.value = 'pending';

    try {
      chartData.value = await fetchChartData(
        {
          charts: codes.value,
          period: props.period,
          start: props.start,
          end: props.end,
          currency: props.currency,
        },
        apiBase,
      );
      loadStatus.value = 'ready';
    } catch {
      // Молча гасим: на месте графика останется плашка «данных нет», а статья
      // из-за одного виджета падать не должна.
      loadStatus.value = 'error';
    }
  };

  // Виджет стоит внутри текста статьи и чаще всего ниже фолда: и запрос, и
  // чанк Highcharts откладываются до подхода блока к вьюпорту.
  const { stop: stopObserver } = useIntersectionObserver(
    root,
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;

      stopObserver();
      load();
    },
    { rootMargin: PRELOAD_MARGIN },
  );

  /*=== Ряды ===*/

  /** Что рисуем, задаёт `data-template`: доходность по цене пая либо СЧА. */
  const tab = computed<ChartWidgetTab>(() =>
    props.template === 'scha' ? 'scha' : 'profit',
  );

  const apiCharts = computed<Record<string, ChartApiSeries>>(
    () => chartData.value?.charts ?? {},
  );

  /** Ряды запрошенных кодов — всё, что бэкенд вернул сверх них, не наше дело. */
  const apiSeriesList = computed<ChartApiSeries[]>(() =>
    codes.value
      .map((code) => apiCharts.value[code])
      .filter((item): item is ChartApiSeries => Boolean(item)),
  );

  const series = computed<ChartSeries[]>(() =>
    codes.value
      .map((code, index) => {
        const apiSeries = apiCharts.value[code];

        return apiSeries ? buildSeries(code, apiSeries, index, tab.value) : null;
      })
      .filter((item): item is ChartSeries => Boolean(item?.points.length)),
  );

  /** СЧА есть не у всех источников: у тикеров старой таблицы `charts` его нет. */
  const hasScha = computed(() =>
    apiSeriesList.value.some((item) =>
      (item.values ?? []).some((point) => Number(point.scha) > 0),
    ),
  );

  /** Валюта ряда: что вернул бэкенд, иначе — что просили в атрибутах. */
  const currency = computed(() => {
    const fromApi = apiSeriesList.value.find((item) => item.currency);

    return normalizeCurrency(fromApi?.currency ?? props.currency ?? '');
  });

  // График СЧА без значений СЧА рисовать нечем — показываем плашку «нет данных».
  const hasData = computed(
    () => Boolean(series.value.length) && (tab.value !== 'scha' || hasScha.value),
  );

  /*=== График ===*/

  const chart = shallowRef<HighchartsNS.Chart | null>(null);
  let highcharts: typeof HighchartsNS | null = null;

  /** Highcharts работает только с DOM — на сервере остаётся пустой контейнер. */
  const renderChart = async () => {
    if (!chartEl.value || !hasData.value) return;

    if (!highcharts) {
      const module = await import('highcharts/highstock');
      highcharts = (module.default ?? module) as typeof HighchartsNS;
    }

    chart.value?.destroy();
    chart.value = highcharts.stockChart(
      chartEl.value,
      buildChartOptions({
        series: series.value,
        tab: tab.value,
        currency: currency.value,
        fill: props.fill !== false,
        t,
      }),
    );
  };

  watch(series, renderChart, { flush: 'post' });

  onBeforeUnmount(() => {
    stopObserver();
    chart.value?.destroy();
    chart.value = null;
  });

  return {
    root,
    chartEl,
    /** Запрос отработал, а рисовать нечего — на месте графика будет плашка. */
    isEmpty: computed(
      () => loadStatus.value === 'error' || (loadStatus.value === 'ready' && !hasData.value),
    ),
  };
};
